import { useSearchParams } from "@solidjs/router";
import {
  IoBriefcaseOutline,
  IoCashOutline,
  IoDocumentTextOutline,
  IoPersonOutline,
  IoRibbonOutline,
} from "solid-icons/io";
import { createMemo, createSignal, Match, Show, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import {
  isCurriculumSectionComplete,
  isPersonalSectionComplete,
  isPricingSectionComplete,
  isSkillsSectionComplete,
  useOnboardingStore,
} from "../context/OnboardingContext";
import { hubSectorLabels, hubSectorState } from "../utils/hub-sector-state";
import { parseEditStep } from "../utils/review-edit";
import { EditStepView } from "./EditStepView";
import { CurriculumModal } from "./modals/CurriculumModal";
import { PersonalModal } from "./modals/PersonalModal";
import { PricingModal } from "./modals/PricingModal";
import { ProfessionalModal } from "./modals/ProfessionalModal";
import { SectorHubModal } from "./modals/SectorHubModal";
import { HubCard, type HubCardProps } from "./shared/HubCard";
import { Review } from "./steps/Preview";

const doneOrTodo = (complete: boolean) => (complete ? "done" : "todo");

export const LinkedInOnboarding = () => {
  const [store] = useOnboardingStore();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const editStep = createMemo(() =>
    store.step === "review" ? parseEditStep(searchParams.edit) : null,
  );
  const [sectorOpen, setSectorOpen] = createSignal(false);
  const [skillsOpen, setSkillsOpen] = createSignal(false);
  const [pricingOpen, setPricingOpen] = createSignal(false);
  const [personalOpen, setPersonalOpen] = createSignal(false);
  const [curriculumOpen, setCurriculumOpen] = createSignal(false);

  // Check completion status for each section using schema validation
  const sectorState = createMemo(() => hubSectorState(store.sector));

  const isSkillsComplete = createMemo(() => isSkillsSectionComplete(store));

  const isPricingComplete = createMemo(() => isPricingSectionComplete(store));

  const isPersonalComplete = createMemo(() => isPersonalSectionComplete(store));

  const isCurriculumComplete = createMemo(() =>
    isCurriculumSectionComplete(store),
  );

  /**
   * The «Settore» card's line (AC24): LinkedIn's proposal to check before the
   * first «Salva», then a summary of the choices; nothing without a primary.
   */
  const sectorDetail = createMemo((): HubCardProps["detail"] => {
    const labels = hubSectorLabels(store.sector.value, {
      category: categoryLabel,
      areas: (count) =>
        count === 1
          ? t("onboarding.linkedinHub.sector.areas_one")
          : t("onboarding.linkedinHub.sector.areas", { count }),
    });
    if (!labels) return undefined;
    return sectorState() === "review"
      ? {
          text: t("onboarding.linkedinHub.sector.proposal", { labels }),
          tone: "proposal",
        }
      : { text: labels, tone: "muted" };
  });

  return (
    <Switch
      fallback={
        <div class="flex flex-col gap-8 w-full">
          <div class="flex flex-col gap-4">
            {/* Sector Card */}
            <SectorHubModal open={sectorOpen()} onOpenChange={setSectorOpen}>
              <HubCard
                icon={IoBriefcaseOutline}
                title={t("onboarding.linkedinHub.sector.title")}
                description={t("onboarding.linkedinHub.sector.description")}
                status={sectorState()}
                detail={sectorDetail()}
              />
            </SectorHubModal>

            {/* Skills Card */}
            <Dialog open={skillsOpen()} onOpenChange={setSkillsOpen}>
              <HubCard
                icon={IoRibbonOutline}
                title={t("onboarding.linkedinHub.skills.title")}
                description={t("onboarding.linkedinHub.skills.description")}
                status={doneOrTodo(isSkillsComplete())}
              />
              <DialogContent class="max-w-4xl">
                <ProfessionalModal onClose={() => setSkillsOpen(false)} />
              </DialogContent>
            </Dialog>

            {/* Pricing Card */}
            <Dialog open={pricingOpen()} onOpenChange={setPricingOpen}>
              <HubCard
                icon={IoCashOutline}
                title="Tariffe e disponibilità"
                description="Imposta la tua tariffa oraria e la tua disponibilità oraria"
                status={doneOrTodo(isPricingComplete())}
              />
              <DialogContent class="max-w-4xl">
                <PricingModal onClose={() => setPricingOpen(false)} />
              </DialogContent>
            </Dialog>

            {/* Personal Card */}
            <Dialog open={personalOpen()} onOpenChange={setPersonalOpen}>
              <HubCard
                icon={IoPersonOutline}
                title="Dati personali & Portfolio"
                description="Completa il tuo profilo con informazioni di contatto, portfolio e link ai tuoi lavori migliori"
                status={doneOrTodo(isPersonalComplete())}
              />
              <DialogContent class="max-w-4xl">
                <PersonalModal onClose={() => setPersonalOpen(false)} />
              </DialogContent>
            </Dialog>

            {/* Curriculum Card */}
            <Dialog open={curriculumOpen()} onOpenChange={setCurriculumOpen}>
              <HubCard
                icon={IoDocumentTextOutline}
                title="Curriculum"
                description="Carica il tuo CV"
                status={doneOrTodo(isCurriculumComplete())}
              />
              <DialogContent class="max-w-2xl">
                <CurriculumModal onClose={() => setCurriculumOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      }
    >
      <Match when={store.step === "review"}>
        <Show when={editStep()} fallback={<Review />}>
          {(step) => <EditStepView step={step()} />}
        </Show>
      </Match>
    </Switch>
  );
};
